/**
 * Portal yoinked from @mui/material v7.3.4
 * (https://github.com/mui/material-ui/blob/v7.3.4/packages/mui-material/src/Portal/Portal.tsx)
 *
 * Modified to ignore SSR/React 19 and allow for container to be a string ID of
 * a specific DOM element.
 *
 * Portal will render the children to container, falling back to the document
 * body if the container is either not provided or not found.  The primary
 * complexity around this component is in the ref management, but this component
 * is set up to allow forwarding a ref either to the container element or to
 * the children if the portal is disabled.
 */

import * as React from "react";
import * as ReactDOM from "react-dom";
import ErrorUtil from "../../../shared/ErrorUtil.js";

type PortalProps = {
	children: React.ReactNode;
	container: Container;
	isPortalDisabled?: boolean;
};

type Container = (() => HTMLElement) | HTMLElement | string | null;

const getContainer: (container: Container) => HTMLElement | null = (
	container: Container
) => {
	if (typeof container === "function") {
		return container();
	} else if (typeof container === "string") {
		const el = document.getElementById(container);
		if (!el) {
			ErrorUtil.log(
				"E_PORTAL_CONTAINER_NOT_FOUND",
				`Portal: Container element with id "${container}" not found`,
				{container}
			);
		}
		return el;
	} else {
		return container;
	}
};

const setRef = <T,>(
	ref:
		| React.MutableRefObject<T | null>
		| ((instance: T | null) => void)
		| null
		| undefined,
	value: T | null
): void => {
	if (typeof ref === "function") {
		ref(value);
	} else if (ref) {
		ref.current = value;
	}
};

export const Portal = React.forwardRef(function Portal(
	props: PortalProps,
	forwardedRef: React.ForwardedRef<Element>
) {
	const {children, container, isPortalDisabled = false} = props;

	// mountNode gets the HTML element into which the children will be rendered
	// when the portal is not disabled
	const [mountNode, setMountNode] = React.useState<
		ReturnType<typeof getContainer>
	>(null);

	// setup the mountNode
	React.useEffect(() => {
		if (!isPortalDisabled) {
			setMountNode(getContainer(container) || document.body);
		}
	}, [container, isPortalDisabled]);

	// handle ref forwarding when the portal is not disabled
	React.useEffect(() => {
		if (mountNode && !isPortalDisabled) {
			setRef(forwardedRef, mountNode);
			return () => {
				setRef(forwardedRef, null);
			};
		}
	}, [forwardedRef, mountNode, isPortalDisabled]);

	// handle ref forwarding when the portal is disabled and render
	// the children without creating a portal
	if (isPortalDisabled) {
		if (React.isValidElement(children)) {
			const newProps = {
				ref: forwardedRef,
			};
			return React.cloneElement(children, newProps);
		}
		return children;
	}

	// render the children to the mountNode if it exists
	return mountNode ? ReactDOM.createPortal(children, mountNode) : null;
});
